/**
 * @module app.cm
 */

import { TaskQueue, autoinject, observable, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm, Sorting, SortDirection } from 'app/shared/lib/essette-site';
import { CaseService } from 'app/cm/services/case-service';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/api/api';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssTableCustomElement, SelectEvent } from 'app/shared/elements/ess-table';
import { MemberService, IDiagnosisSearchFilters } from 'app/cm/services/member-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { deepClone } from 'app/shared/lib/utilities';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import { getMatcherFunction, MatcherFunction } from 'app/shared/lib/matcher';
import * as CD from 'app/cd/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class SaveCaseDiagnoses extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    private case: CM.Case | null = null;

    public get memberId(): string | null {
        return this.case?.member?.id || null;
    }

    @observable({ changeHandler: 'deselectTables' })
    protected searchOption: string = 'searchClaims';

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;
    @bindable
    protected diagnoses: ReadonlyArray<CM.MemberDiagnosis> = [];

    public selectedDiagnosisStore: Map<string, string> = new Map<string, string>();

    public claimsDiagnoses: ReadonlyArray<CM.MemberDiagnosis> = [];
    protected claimsPageSize: number = 25;
    private claimsSorting: Sorting = new Sorting();
    protected claimsResultCount: number = 0;

    public searchResults: CD.Diagnosis[] = [];
    protected searchString: string = '';
    protected searchFilters: IDiagnosisSearchFilters = {
        searchString: '',
        diagCode: null,
        diagnosis: null
    };
    protected searchResultCount: number = 0;
    protected searchPageSize: number = 25;
    private searchSorting: Sorting = new Sorting();

    protected readonly: boolean = false;
    private subscriptions: Array<Subscription> = [];
    protected memberDiagnosisMatcher: MatcherFunction = getMatcherFunction('diagnosis.id');
    protected diagnosisMatcher: MatcherFunction = getMatcherFunction('id');

    ////****************** Element References ***************************************////
    public $claimsTable!: EssTableCustomElement<CM.MemberDiagnosis>;
    public $searchTable!: EssTableCustomElement<CD.Diagnosis>;
    protected $claimsPager!: EssPagerCustomElement;
    protected $diagnosesPager!: EssPagerCustomElement;
    protected $searchPager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly caseService: CaseService,
        private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        private readonly queue: TaskQueue,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.caseId) throw new Error('Missing or invalid caseId parameter');
        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const isSuccessful = await this.saveDiagnoses();
        this.searchOption = 'searchClaims';
        return isSuccessful;
    }

    protected selectChanged({ detail }: SelectEvent<CM.MemberDiagnosis & CD.Diagnosis>): void {
        // determine whether to use $claimsTable<CM.MemberDiagnosis> or $searchTable<CD.Diagnosis>
        // the data between these two tables are shaped differently
        const useClaimsTable = this.searchOption === 'searchClaims';
        const diagnosisId = useClaimsTable ? detail.item?.diagnosis?.id : detail.item?.id;

        // if only one checkbox was checked or unchecked...
        if(diagnosisId) {
            // determine if item was unchecked and so should be deleted from store
            const itemUnchecked = detail.items.findIndex(i => (i.diagnosis?.id ?? i.id) === diagnosisId) == -1;
            if(itemUnchecked) {
                this.selectedDiagnosisStore.delete(diagnosisId);
                // otherwise item has been checked and should be added to store
            } else {
                const diagnosisCode = useClaimsTable ? detail.item?.diagnosis?.code : detail.item?.code;
                const diagnosisDescription = useClaimsTable ? detail.item?.diagnosis?.description : detail.item?.description;
                this.selectedDiagnosisStore.set(diagnosisId, `${diagnosisCode} - ${diagnosisDescription}`);
            }
            // if no single item ID, then multiple rows may have been selected at once...
        } else if(detail.items.length) {
            detail.items.forEach(i => {
                const diagnosisId = useClaimsTable ? i.diagnosis?.id : i.id;
                const diagnosisCode = useClaimsTable ? i.diagnosis?.code : i.code;
                const diagnosisDescription = useClaimsTable ? i.diagnosis?.description : i.description;
                if(diagnosisId) this.selectedDiagnosisStore.set(diagnosisId, `${diagnosisCode} - ${diagnosisDescription}`);
            });
            // if no single item and no items, then all rows were deselected at once...
        } else if(useClaimsTable) {
            // so remove everything on the current page from the selection
            this.claimsDiagnoses.forEach(cd => {
                if(cd.diagnosis?.id) this.selectedDiagnosisStore.delete(cd.diagnosis.id);
            });
        } else {
            this.searchResults.forEach(sr => {
                if(sr.id) this.selectedDiagnosisStore.delete(sr.id);
            });
        }
    }

    ////****************** Events ***************************************************////
    protected deselectTables(): void {
        if(this.$searchTable) this.$searchTable.deselectAll();
        if(this.$claimsTable) this.$claimsTable.deselectAll();
        this.selectedDiagnosisStore = new Map<string, string>();
    }

    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {
            await this.accountContext.load();
            const canEditClosedCase = this.accountContext.hasPermission('CM.Case.Duty.EditClosedCase');
            this.readonly = caseIsReadOnly(e.case, this.env.user.id) || this.readonly;
            this.reload(this.caseId);
        };
    }

    ////****************** Functions ***********************************************////

    protected applyClaimsSort(event: SortChangeEvent): void {
        if(!this.memberId) { return; }
        const { property, direction } = event.detail;
        const { claimsSorting } = this;
        if(direction === null) {
            claimsSorting.remove(property);
        } else {
            claimsSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.loadMemberClaimDiagnoses();
    }

    public async reload(caseId: string | null): Promise<void> {
        if(!caseId && this.context.case) {
            this.case = deepClone(this.context.case);
        }
        if(caseId) {
            await this.loadCase(caseId);
        }

        if(!this.case) { return; }

        if(!this.readonly) await this.loadMemberClaimDiagnoses();

    }

    protected clear() {
        this.deselectTables();
        this.searchFilters.searchString = '';
        this.searchString = '';
        this.searchResults.splice(0);
        this.searchResultCount = 0;
    }

    private restoreSelected() {
        if(!this.selectedDiagnosisStore.size) return;
        const useClaimsTable = this.searchOption === 'searchClaims';
        if(useClaimsTable) {
            // convert the store to a type appropriate for $claimsTable
            const selectedMemberDiagnoses: CM.MemberDiagnosis[] = Array.from(this.selectedDiagnosisStore, ([key, _value]) => <CM.MemberDiagnosis>{ diagnosis: { id: key } });
            // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
            this.queue.queueTask(() => {
                if(this.$claimsTable) this.$claimsTable.restoreSelected(selectedMemberDiagnoses);
            });
        } else {
            // convert the store to a type appropriate for $searchTable
            const selectedDiagnoses: CD.Diagnosis[] = Array.from(this.selectedDiagnosisStore, ([key, _value]) => <CD.Diagnosis>{ id: key });
            // Give aurelia a chance to bind data to the table before we ask the table to restore selected items
            this.queue.queueTask(() => {
                if(this.$searchTable) this.$searchTable.restoreSelected(selectedDiagnoses);
            });
        }
    }

    protected applySearchSort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { searchSorting } = this;
        if(direction === null) {
            searchSorting.remove(property);
        } else {
            searchSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.search(false);
    }

    public reset() {
        this.clear();
        this.searchOption = 'searchClaims';
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    protected async saveDiagnoses(): Promise<boolean> {
        if(!this.memberId) return false;
        const promises: Promise<IApiActionResults>[] = [];
        let isSuccessful: boolean = false;
        Array.from(this.selectedDiagnosisStore.keys()).map(diag => {
            return {
                member: { id: this.memberId },
                isActive: true,
                isPrincipal: false,
                diagnosis: {
                    id: diag
                }
            } as CM.MemberDiagnosis;
        }).forEach(memberDiag => promises.push(this.service.insertMemberDiagnosis(memberDiag)));

        try {
            this.state.beginSave();
            // Execute all promises and wait until they all finish
            const responses = await Promise.all(promises);
            isSuccessful = responses.every(r => { return r.isSuccessful });
            this.toaster.standard.saved('cm:memberDiagnosis.$long', isSuccessful, responses.length);

            this.clear();
            await Promise.resolve(this.loadMemberClaimDiagnoses());
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
            return isSuccessful;
        }
    }

    @apiCall
    public async loadMemberClaimDiagnoses(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.claimsPageSize,
            page: this.$claimsPager?.page || 0,
            sorting: this.claimsSorting
        };

        const includes: string[] = ['diagnosis'];

        const fields: string[] = [
            'claims.count',
            'diagnosis.code',
            'diagnosis.description',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();

            let excluded: string[] = this.diagnoses.map(d => d.diagnosis && d.diagnosis.id || '');
            const response = await this.service.loadDiagnosisClaimsByMember(this.memberId, excluded, options, includes, fields);
            this.claimsDiagnoses = response.results.map((claimDiag) => ({
                ...claimDiag,
                code: claimDiag.diagnosis?.code // need to expose code for row select tooltip
            }));
            this.claimsResultCount = response.count || 0;
            this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async search(newSearch: boolean = true): Promise<void> {
        if(newSearch && this.searchString.length >= 2) this.searchFilters.searchString = this.searchString;
        if(!this.memberId || !this.searchFilters.searchString) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.searchPageSize,
            page: this.$searchPager?.page || 0,
            sorting: this.searchSorting
        };

        const fields: string[] = [
            'code',
            'description',
            'memberClaims.count'
        ];

        try {
            this.state.beginLoad();

            let excluded: string[] = this.diagnoses.map(d => d.diagnosis && d.diagnosis.id || '');
            const response = await this.service.filterDiagnoses(this.memberId, this.searchFilters, this.env.now(), excluded, options, [], fields);
            this.searchResults = response.results;
            this.searchResultCount = response.count || 0;
            this.restoreSelected();
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadCase(caseId: string): Promise<void> {
        if(this.context.case && this.context.case.id === caseId) {
            this.case = this.context.case;
            return;
        }

        const includes: string[] = ['member'];

        try {
            this.state.beginLoad();
            const response = await this.caseService.loadCase(caseId, includes);
            this.case = response.results[0];
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

}
